/**
 * BoundaryPanel — V98-3-2, the Scope tab of a program (ADR-0048 §3–§4).
 *
 * Per program and period: systems in / out of the boundary, the external
 * services those systems send data to with coverage per period (from
 * coverage documents only — never green without a document), and the gaps
 * derived from both, each with its action (no dead ends).
 *
 * Reads (any member): GET programs/:program/boundary?period_id=,
 *   programs/:program/external-services, systems.
 * Writes (program lead or owner/admin — the server enforces; others get
 *   AskAdmin): POST boundary/systems, external-services (+ link systems),
 *   external-services/:id/documents.
 *
 * V98-3 review U12/U13: failures show visibly (not only in the live region),
 * every action's accessible name starts with its visible label, focus returns
 * to the opener when the inline document form closes, and providers show by
 * display name (lib/providers).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, Select, Spinner, StatusGlyph } from '@aigovops/design-system';
import {
  addCoverageDocument,
  addExternalService,
  addSystemToBoundary,
  getProgramBoundary,
  listProgramExternalServices,
  listWorkspaceSystems,
  type CoverageDocumentKind,
  type ExternalServiceView,
  type ProgramCard,
} from '../../lib/api';
import { invalidationMap, queryKeys } from '../../lib/query-keys';
import { fmtProgramDate } from '../../lib/program-shell';
import { onboardingHref } from '../../lib/onboarding-model';
import { getProgramManifest, type ProgramManifest } from '@aigovops/core';
import { AskAdmin } from '../AskAdmin';
import { providerName } from '../../lib/providers';

const KIND_LABEL: Record<CoverageDocumentKind, string> = {
  soc2_type2: 'SOC 2 Type II report',
  soc2_type1: 'SOC 2 Type I report',
  bridge_letter: 'Bridge letter',
  fedramp_authorization: 'FedRAMP authorization',
  iso_27001: 'ISO 27001 certificate',
  iso_42001: 'ISO 42001 certificate',
  other: 'Other',
};

type Coverage = ExternalServiceView['coverage'][number];
const yesNo = (v: boolean | null) => (v === null ? 'Not answered' : v ? 'Yes' : 'No');

function CoverageCell({ c, periodLabel }: { c: Coverage; periodLabel: string }) {
  const [glyph, text] = c.status === 'covered' ? ['check', 'Covered'] as const
    : c.status === 'partial' ? ['warning', `Partial · gap from ${fmtProgramDate(c.gap_from ?? c.window.start)}`] as const
    : c.status === 'not_received' ? ['x', 'No document'] as const
    : ['check', 'Not needed'] as const;
  return <span className="gp-cov"><StatusGlyph kind={glyph} /> <span>{periodLabel}: {text}</span></span>;
}

export function BoundaryPanel({ slug, program, manifest, card, canManage }: {
  slug: string;
  program: string;
  /** The program's manifest (defaults to the registry entry for `program`). */
  manifest?: ProgramManifest;
  card: ProgramCard;
  canManage: boolean;
}) {
  const m = manifest ?? getProgramManifest(program);
  // ADR-0050: the coverage documents a program accepts; none = the program has no provider coverage.
  const kinds: readonly CoverageDocumentKind[] = m?.coverage.documentKinds ?? [];
  const hasCoverage = kinds.length > 0;
  const queryClient = useQueryClient();
  const periods = card.periods.filter((p) => p.active && p.period_id);
  const [periodId, setPeriodId] = useState<string | null>(periods[0]?.period_id ?? null);
  const [live, setLive] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [docFor, setDocFor] = useState<string | null>(null);
  const inHeading = useRef<HTMLHeadingElement>(null);
  const gapsHeading = useRef<HTMLHeadingElement>(null);

  const boundary = useQuery({
    queryKey: queryKeys.programBoundary(slug, program, periodId),
    queryFn: () => getProgramBoundary(slug, program, periodId),
  });
  const services = useQuery({
    queryKey: queryKeys.programExternalServices(slug, program),
    queryFn: () => listProgramExternalServices(slug, program),
    enabled: hasCoverage,
  });
  const systems = useQuery({ queryKey: queryKeys.systems(slug), queryFn: () => listWorkspaceSystems(slug), staleTime: 60_000 });

  const refresh = () => Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  const say = (m: string, focus: React.RefObject<HTMLHeadingElement>) => {
    setLive(''); setError(null); window.setTimeout(() => { setLive(m); focus.current?.focus(); }, 50);
  };

  if (boundary.isLoading) return <Spinner label="Loading boundary" />;
  if (boundary.error || !boundary.data) {
    return <Alert variant="error"><p>The boundary could not load.</p><Button onClick={() => void boundary.refetch()}>Retry</Button></Alert>;
  }
  const inside = boundary.data.boundary.systems;
  const insideIds = new Set(inside.map((s) => s.system_id));
  const outside = (systems.data?.systems ?? []).filter((s) => !insideIds.has(s.system_id));
  const register = services.data?.external_services ?? [];
  const registered = new Set(register.map((s) => s.provider_key));
  const unlisted = new Map<string, string[]>();
  for (const s of inside) for (const p of s.providers) if (!registered.has(p)) unlisted.set(p, [...(unlisted.get(p) ?? []), s.system_id]);
  const nameOf = (id: string) => inside.find((s) => s.system_id === id)?.name ?? id;
  const periodLabel = (id: string | null) => card.periods.find((p) => p.period_id === id)?.label ?? 'Today';
  const coverageGaps = register.flatMap((svc) => svc.coverage
    .filter((c) => c.status === 'partial' || c.status === 'not_received')
    .map((c) => ({ svc, c })));
  const federalUnanswered = m?.coverage.federalDataGaps ? inside.filter((s) => s.handles_federal_data === null) : [];
  const gapCount = coverageGaps.length + unlisted.size + federalUnanswered.length;

  const ask = (what: string) => <AskAdmin slug={slug} compact verb={what} what="manages program boundaries" subject={`GAC Pro ${card.program}: ${what}`} />;

  const addSystem = async (systemId: string, name: string) => {
    try {
      await addSystemToBoundary(slug, program, { system_id: systemId, period_id: periodId });
      await refresh();
      say(`${name} added to the boundary.`, inHeading);
    } catch (e) { const m = e instanceof Error ? e.message : 'The system could not be added.'; setError(m); setLive(m); }
  };
  const addProvider = async (key: string, systemIds: string[]) => {
    try {
      await addExternalService(slug, program, { name: providerName(key), provider_key: key, link_system_ids: systemIds });
      await refresh();
      say(`${providerName(key)} added to the external-services register.`, gapsHeading);
    } catch (e) { const m = e instanceof Error ? e.message : 'The service could not be added.'; setError(m); setLive(m); }
  };

  return (
    <div className="gp-tab-panel">
      <div role="status" aria-live="polite" className="sr-only">{live}</div>
      {error ? <Alert variant="error" onDismiss={() => setError(null)}>{error}</Alert> : null}
      {periods.length > 1 || (periods.length === 1 && boundary.data.boundary.source !== 'period') ? (
        <div className="gp-period-pick">
          <Field id="boundary-period" label="Period">
            <Select value={periodId ?? ''} onChange={(e) => setPeriodId(e.currentTarget.value || null)}>
              {periods.map((p) => <option key={p.period_id!} value={p.period_id!}>{p.label}</option>)}
              <option value="">Standing boundary</option>
            </Select>
          </Field>
        </div>
      ) : null}

      <Card>
        <h3 ref={inHeading} tabIndex={-1} id="boundary-in" className="gp-card-h">{`In the boundary · ${inside.length}`}</h3>
        {inside.length === 0
          ? <EmptyState title="No systems in this boundary" action={canManage
              ? (outside[0] ? <Button variant="primary" onClick={() => void addSystem(outside[0]!.system_id, outside[0]!.name)}>{`Add ${outside[0]!.name}`}</Button>
                : <Link to={onboardingHref(slug, 'first_system')}>Register a system</Link>)
              : ask('add systems to the boundary')} />
          : (
            <table className="gp-table" role="table" aria-labelledby="boundary-in">
              <thead role="rowgroup"><tr role="row">
                <th role="columnheader" scope="col">System</th>
                <th role="columnheader" scope="col">Federal data</th>
                <th role="columnheader" scope="col">Internet-facing</th>
                <th role="columnheader" scope="col">Sends data to</th>
              </tr></thead>
              <tbody role="rowgroup">
                {inside.map((s) => (
                  <tr role="row" key={s.system_id}>
                    <td role="cell" data-label="System"><Link to={`/w/${slug}/app/systems/${encodeURIComponent(s.system_id)}`}>{s.name}</Link></td>
                    <td role="cell" data-label="Federal data">{yesNo(s.handles_federal_data)}</td>
                    <td role="cell" data-label="Internet-facing">{yesNo(s.internet_reachable)}</td>
                    <td role="cell" data-label="Sends data to">{s.providers.length ? s.providers.map(providerName).join(', ') : <span className="gp-dim">None listed</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>

      {outside.length > 0 ? (
        <Card>
          <h3 id="boundary-out" className="gp-card-h">{`Not in the boundary · ${outside.length}`}</h3>
          <ul className="gp-gap-list" aria-labelledby="boundary-out">
            {outside.map((s) => (
              <li key={s.system_id} className="gp-gap">
                <Link to={`/w/${slug}/app/systems/${encodeURIComponent(s.system_id)}`}>{s.name}</Link>
                {canManage ? <Button compact variant="ghost" onClick={() => void addSystem(s.system_id, s.name)} aria-label={`Add to boundary · ${s.name}`}>Add to boundary</Button> : null}
              </li>
            ))}
          </ul>
          {!canManage ? ask('add a system to the boundary') : null}
        </Card>
      ) : null}

      {hasCoverage ? (
        <Card>
          <h3 id="boundary-ext" className="gp-card-h">External services</h3>
          {services.isLoading ? <Spinner label="Loading external services" />
            : services.error ? <Alert variant="error"><p>External services could not load.</p><Button onClick={() => void services.refetch()}>Retry</Button></Alert>
            : register.length === 0
              ? <EmptyState title="No external services registered" action={<Link to={`/w/${slug}/app/systems`}>List providers on your systems</Link>} />
              : (
                <table className="gp-table" role="table" aria-labelledby="boundary-ext">
                  <thead role="rowgroup"><tr role="row">
                    <th role="columnheader" scope="col">Service</th>
                    <th role="columnheader" scope="col">Used by</th>
                    <th role="columnheader" scope="col">Coverage</th>
                  </tr></thead>
                  <tbody role="rowgroup">
                    {register.map((svc) => (
                      <tr role="row" key={svc.external_service_id}>
                        <td role="cell" data-label="Service">{svc.name}</td>
                        <td role="cell" data-label="Used by">{svc.used_by.length ? svc.used_by.map((u) => u.name).join(', ') : <span className="gp-dim">No system</span>}</td>
                        <td role="cell" data-label="Coverage">
                          {svc.coverage.map((c) => <div key={c.period_id ?? 'today'}><CoverageCell c={c} periodLabel={periodLabel(c.period_id)} /></div>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </Card>
      ) : null}

      <Card>
        {/* V98-4 review: inside AI Regulatory the gaps are external services not in the register. */}
        <h3 ref={gapsHeading} tabIndex={-1} id="boundary-gaps" className="gp-card-h">{`${hasCoverage ? 'Gaps' : 'Not in the register'} · ${gapCount}`}</h3>
        {gapCount === 0 ? <p className="gp-tab-sub">No coverage, register or federal-data gaps.</p> : (
          <ul className="gp-gap-list" aria-labelledby="boundary-gaps">
            {coverageGaps.map(({ svc, c }) => (
              <li key={`${svc.external_service_id}:${c.period_id ?? 'today'}`} className="gp-gap">
                <span><strong>{svc.name}</strong> · {periodLabel(c.period_id)} · {c.gaps.length ? c.gaps.map((g) => `${fmtProgramDate(g.from)} – ${fmtProgramDate(g.to)}`).join(', ') : 'no document'}</span>
                {canManage
                  ? docFor === `${svc.external_service_id}:${c.period_id}`
                    ? <CoverageDocForm slug={slug} program={program} kinds={kinds} serviceId={svc.external_service_id} name={svc.name} window={c.window}
                        onCancel={() => { setDocFor(null); window.setTimeout(() => document.getElementById(`doc-open-${svc.external_service_id}-${c.period_id ?? 'today'}`)?.focus(), 0); }}
                        onSaved={async () => { setDocFor(null); await refresh(); say(`Coverage document recorded for ${svc.name}.`, gapsHeading); }} />
                    : <Button compact variant="secondary" id={`doc-open-${svc.external_service_id}-${c.period_id ?? 'today'}`} aria-label={`Add coverage document · ${svc.name}`} onClick={() => setDocFor(`${svc.external_service_id}:${c.period_id}`)}>Add coverage document</Button>
                  : ask(`record coverage for ${svc.name}`)}
              </li>
            ))}
            {[...unlisted.entries()].map(([key, ids]) => (
              <li key={`unlisted:${key}`} className="gp-gap">
                <span><strong>{providerName(key)}</strong> · used by {ids.map(nameOf).join(', ')} · not in the register</span>
                {canManage ? <Button compact variant="secondary" aria-label={`Add to register · ${providerName(key)}`} onClick={() => void addProvider(key, ids)}>Add to register</Button> : ask(`add ${providerName(key)} to the register`)}
              </li>
            ))}
            {federalUnanswered.map((s) => (
              <li key={`fed:${s.system_id}`} className="gp-gap">
                <span><strong>{s.name}</strong> · federal data not answered</span>
                <Link to={`/w/${slug}/app/systems/${encodeURIComponent(s.system_id)}`}>Answer on the system</Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function CoverageDocForm({ slug, program, kinds, serviceId, name, window: win, onCancel, onSaved }: {
  slug: string; program: string; kinds: readonly CoverageDocumentKind[]; serviceId: string; name: string;
  window: { start: string; end: string };
  onCancel: () => void; onSaved: () => void | Promise<void>;
}) {
  const [kind, setKind] = useState<CoverageDocumentKind>(kinds[0] ?? 'other');
  const [from, setFrom] = useState(win.start);
  const [to, setTo] = useState(win.end);
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // R3-U24: the form takes focus on its first field when it opens (focus used to drop to <body>).
  const firstField = useRef<HTMLSelectElement>(null);
  useEffect(() => { firstField.current?.focus(); }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (saving) return;
    const errs: Record<string, string> = {};
    if (!from) errs.from = 'Choose the first day it covers.';
    if (!to) errs.to = 'Choose the last day it covers.';
    else if (from && to < from) errs.to = 'Ends on or after the start.';
    if (!reference.trim()) errs.reference = 'Name the document or where it is kept.';
    setErrors(errs); if (Object.values(errs).some(Boolean)) return;
    setSaving(true); setFormError(null);
    try {
      await addCoverageDocument(slug, program, serviceId, { kind, covers_from: from, covers_to: to, reference: reference.trim() });
      await onSaved();
    } catch (err) { setFormError(err instanceof Error ? err.message : 'The document could not be recorded.'); }
    finally { setSaving(false); }
  };
  return (
    <form className="gp-inline-form" onSubmit={submit} noValidate aria-label={`Coverage document for ${name}`}>
      {formError ? <Alert variant="error">{formError}</Alert> : null}
      <Field id={`cov-kind-${serviceId}`} label="Document">
        <Select ref={firstField} value={kind} onChange={(e) => { setKind(e.currentTarget.value as CoverageDocumentKind); setFormError(null); }}>
          {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </Select>
      </Field>
      <Field id={`cov-from-${serviceId}`} label="Covers from" required error={errors.from}>
        <Input type="date" value={from} onChange={(e) => { setFrom(e.currentTarget.value); setFormError(null); setErrors((x) => ({ ...x, from: '' })); }} />
      </Field>
      <Field id={`cov-to-${serviceId}`} label="Covers to" required error={errors.to}>
        <Input type="date" value={to} onChange={(e) => { setTo(e.currentTarget.value); setFormError(null); setErrors((x) => ({ ...x, to: '' })); }} />
      </Field>
      <Field id={`cov-ref-${serviceId}`} label="Reference" required error={errors.reference}>
        <Input value={reference} onChange={(e) => { setReference(e.currentTarget.value); setFormError(null); setErrors((x) => ({ ...x, reference: '' })); }} />
      </Field>
      <div className="gp-actions">
        <Button type="submit" variant="primary" aria-disabled={saving || undefined}>{saving ? 'Saving…' : 'Record document'}</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
